import React, { FunctionComponent, useEffect, useState } from 'react'
import styles from './style.module.css'
import { Button, Heading, Icon, Link, Message, Text, DesignLanguage, useIsDesignLanguage, useToast } from '@afterpaytouch/core'
import { Route } from '../../config/router'
import {
  selectPurchaseMethod,
  setCheckoutError,
  setCheckoutStatus,
  setPaymentOptionsError,
  useAppDispatch,
  useAppropriatePaymentMethod,
  useAppRouter,
  useCheckoutState,
  usePaymentOptionsError,
  usePBIRejected,
  setShowPaymentOptionsErrorModal,
} from '../../state'
import { CheckoutStatus, TerminalErrorCode } from '../../model'
import { useMonitoringWithEnduringEventProperties } from '../../integrations/monitoring'
import { TrackingEvent } from '../../model/monitoring'
import { format } from 'date-fns'
import { useDate } from '@afterpaytouch/form/lib/utils/date'
import { ErrorCode } from '@afterpaytouch/portal-api'
import { useTranslation } from '../../utils/convergence'
import { Trans } from '../../utils/convergence'

type ClickedButton = 'retry' | 'continue' | null

export const RetryCreditFreeze: FunctionComponent = () => {
  const { t } = useTranslation()
  const router = useAppRouter()
  const dispatch = useAppDispatch()
  const paymentMethod = useAppropriatePaymentMethod()
  const [clickedButton, setClickedButton] = useState<ClickedButton>(null)
  const { started } = useCheckoutState()
  const installmentsCount = started?.paymentSchedules?.length ?? 4
  const paymentOptionsError = usePaymentOptionsError()
  const { parseISODateString } = useDate('h:mm aa MM/dd/yyyy')

  const { logEvent } = useMonitoringWithEnduringEventProperties()
  const setToast = useToast()
  const isPbiRejected = usePBIRejected()

  const currentDate = new Date()
  const creditFreezeApplicationDeadline =
    paymentOptionsError?.extra?.creditFreezeApplicationDeadline ?? new Date(currentDate.setMinutes(currentDate.getMinutes() + 20)).toISOString()
  // Actual DateTime considering timezones
  const creditFreezeApplicationDeadlineDate = new Date(creditFreezeApplicationDeadline)

  // Remove the timezone from the string so the date can be parsed without timezone e.g. 2024-05-16T23:59:59.000-04:00 -> 2024-05-16T23:59:59.000
  const creditFreezeApplicationDeadlineWithoutTimezone = creditFreezeApplicationDeadline.replace(/([-+]\d{2}:\d{2})$/, '')
  const dateWithoutTimezone = new Date(creditFreezeApplicationDeadlineWithoutTimezone)
  const orderDate = dateWithoutTimezone.setMinutes(dateWithoutTimezone.getMinutes() - 20)
  const formattedCurrentDate = format(orderDate, 'dd MMM yyyy')
  const isBronteDesignLangauge = useIsDesignLanguage(DesignLanguage.Bronte)

  const tooManyRequests = (): JSX.Element => (
    <>
      <Text size='M' bold testNameSpace='too-many-requests-header'>
        {t('consumerLending:retryCreditFreeze.toast.header')}
      </Text>
      <Text size='M'>{t('consumerLending:retryCreditFreeze.toast.subText')}</Text>
    </>
  )

  useEffect(() => {
    if (paymentOptionsError?.extra?.tooManyRequests === true) {
      setToast({
        message: tooManyRequests,
        kind: 'Failure',
        toastTime: 10000,
        testNameSpace: 'too-many-requests-toast',
        onShow: () => logEvent(TrackingEvent.VIEWED_CL_CREDIT_FREEZE_TOO_MANY_REQUESTS_TOAST),
      })
    }
  }, [])

  const routeToSummary = (): void => {
    if (paymentMethod === null) {
      dispatch(setCheckoutStatus(CheckoutStatus.LackOfEligiblePaymentMethod))
      router.push(Route.ADD_PAYMENT_METHOD)
      return
    }
    dispatch(selectPurchaseMethod(paymentMethod, false))
    router.push(Route.SUMMARY)
  }

  const onRetry = async (): Promise<void> => {
    setClickedButton('retry')
    logEvent(TrackingEvent.PRESSED_CL_CREDIT_FREEZE_RETRY_BUTTON)
    if (new Date().getTime() > creditFreezeApplicationDeadlineDate.getTime()) {
      logEvent(TrackingEvent.CL_CREDIT_FREEZE_APPLICATION_DEADLINE_PASSED)
      if (isPbiRejected) {
        dispatch(
          setCheckoutError({
            error: TerminalErrorCode.ErrorPCL,
            errorCode: ErrorCode.Error,
          })
        )
        router.push(Route.TERMINAL_ERROR)
      } else {
        dispatch(setShowPaymentOptionsErrorModal(true))
        dispatch(
          setPaymentOptionsError({
            error: TerminalErrorCode.ErrorPCL,
            errorCode: ErrorCode.Error,
          })
        )
        routeToSummary()
      }
    } else {
      router.push(Route.PAYMENT_OPTIONS)
    }
  }

  const onContinueWithPbi = async (): Promise<void> => {
    setClickedButton('continue')
    logEvent(TrackingEvent.PRESSED_CL_CREDIT_FREEZE_CONTINUE_WITH_PAY_IN_4_BUTTON)
    routeToSummary()
  }

  const renderInstructions = (): JSX.Element => {
    const mkLink = (url?: string): JSX.Element => {
      return typeof url !== 'undefined' ? <Link key={`retry-credit-freeze-link`} target='_blank' kind='Underline' href={url} /> : <></>
    }

    return (
      <>
        <div className={styles.instructionsWithIcon}>
          <div className={styles.iconWrapper}>
            <Icon name='Profile' size='Small' />
          </div>
          <Text renderAs='div' testNameSpace={`alt-option`}>
            <Trans
              i18nKey={t('consumerLending:retryCreditFreeze.instructions')}
              components={{ a: mkLink(t('consumerLending:retryCreditFreeze.unfreezeCreditFileUrl')) }}
            />
          </Text>
        </div>
      </>
    )
  }

  const renderDescription = (): JSX.Element => {
    return (
      <div className={styles.descriptionContainer}>
        <div>
          <Text size='S'>{t('consumerLending:retryCreditFreeze.information.name')}</Text>
          <Text size='S'>{t('consumerLending:retryCreditFreeze.information.addressLine1')}</Text>
          <Text size='S'>{t('consumerLending:retryCreditFreeze.information.addressLine2')}</Text>
          <Text size='S'>{t('consumerLending:retryCreditFreeze.information.phoneNumber')}</Text>
        </div>
        <div>
          <Text size='S'>{formattedCurrentDate}</Text>
        </div>
        <Text size='S'>{t('consumerLending:retryCreditFreeze.information.description1')}</Text>
        <Text size='S'>
          {t('consumerLending:retryCreditFreeze.information.description2', {
            deadlineDate: parseISODateString(creditFreezeApplicationDeadlineWithoutTimezone).formattedDate,
          })}
        </Text>
      </div>
    )
  }

  return (
    <>
      <div className={styles.container}>
        <Heading size='XXL'>{t(`consumerLending:retryCreditFreeze.heading`)}</Heading>
        <Text>{t('consumerLending:retryCreditFreeze.headingSubText')}</Text>
        {renderInstructions()}
        <Message kind='Basic' iconName='Info' heading={t(`consumerLending:retryCreditFreeze.information.heading`)} description={renderDescription()} />
        <div className={styles.buttonContainer}>
          <Button
            loading={clickedButton === 'retry'}
            padding='Fluid'
            disabled={clickedButton !== null}
            testNameSpace='retry-credit-freeze-retry'
            onClick={onRetry}
          >
            {t(`consumerLending:retryCreditFreeze.retryButton`)}
          </Button>
          {!isPbiRejected && (
            <Button
              loading={clickedButton === 'continue'}
              kind={isBronteDesignLangauge ? 'Tertiary' : 'Secondary'}
              padding='Fluid'
              disabled={clickedButton !== null}
              testNameSpace='retry-credit-freeze-continue-pbi'
              onClick={onContinueWithPbi}
            >
              {t(`consumerLending:retryCreditFreeze.continueWithPbiButton`, { interval: installmentsCount })}
            </Button>
          )}
        </div>
      </div>
    </>
  )
}
